import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IPackagingDispatchDetailedItem,
  IPackagingDispatchListItem,
  IPackagingInsufficientInventoryItem,
} from 'src/types/packaging-dispatch';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  dispatches: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IPackagingDispatchListItem[];
  };
  dispatch: IPackagingDispatchDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  dispatches: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  dispatch: null,
};

const packagingDispatchSlice = createSlice({
  name: 'packagingDispatch',
  initialState,
  reducers: {
    removePackagingDispatch: (state, action) => {
      const { dispatchId } = action.payload;

      const index = state.dispatches.items.findIndex((item) => item._id === dispatchId);

      if (index > -1) {
        action.payload.index = index;
        state.dispatches.items = state.dispatches.items.filter(
          (item) => item._id !== dispatchId
        );
      }
    },
    rollbackPackagingDispatchDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.dispatches.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPackagingDispatches.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingDispatches.fulfilled, (state, action) => {
        state.status = 'success';
        state.dispatches = action.payload;
      })
      .addCase(getPackagingDispatches.rejected, (state, action) => {
        state.dispatches = initialState.dispatches;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getPackagingDispatchById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingDispatchById.fulfilled, (state, action) => {
        state.status = 'success';
        state.dispatch = action.payload;
        state.error = initialState.error;
      })
      .addCase(getPackagingDispatchById.rejected, (state, action) => {
        state.dispatch = initialState.dispatch;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default packagingDispatchSlice.reducer;

export const { removePackagingDispatch, rollbackPackagingDispatchDeletion } =
  packagingDispatchSlice.actions;

// packagingItem/product filters were dropped — the header list has no
// item-level fields to filter by now (see searchPackagingDispatchItems).
export const getPackagingDispatches = createAsyncThunk(
  'packagingDispatch/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    issuedBy?: string | null;
    receivedBy?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, issuedBy, receivedBy, startDate, endDate, sortBy, sort } =
      params;

    try {
      const response = await axios.get(
        `packaging/dispatch?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&issuedBy=${issuedBy ?? ''}&receivedBy=${receivedBy ?? ''}&startDate=${
          startDate ? startDate.toISOString() : ''
        }&endDate=${endDate ? endDate.toISOString() : ''}&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getPackagingDispatchById = createAsyncThunk(
  'packagingDispatch/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`packaging/dispatch/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deletePackagingDispatch = createAsyncThunk(
  'packagingDispatch/delete',
  async (params: { item: IPackagingDispatchListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`packaging/dispatch/${item._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackPackagingDispatchDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TPackagingDispatchItemInput {
  // Present when editing an existing dispatch and this line is being kept
  // (its stock delta is adjusted in place server-side, since a Return line
  // may already reference this dispatch item's id). Always absent on
  // create, and absent on update for a brand-new line.
  _id?: string;
  packagingItem: string;
  product: string;
  quantity: number;
  // Employee who received this specific line — per-line, not per-dispatch.
  receivedBy: string;
}

export interface TCreatePackagingDispatch {
  issuedBy: string;
  dispatchDate: Date;
  remarks: string | null;
  items: TPackagingDispatchItemInput[];
}

export type TUpdatePackagingDispatch = TCreatePackagingDispatch;

// Shape of the rejected value when create()/update() fails on
// insufficient inventory — rejectWithValue is used (rather than letting
// the thunk just throw) because Redux Toolkit's default error handling
// only keeps name/message/stack off a thrown Error, silently dropping the
// `.data` (IPackagingInsufficientInventoryItem[]) the axios interceptor
// attached; rejectWithValue's payload survives untouched through
// .unwrap().
export interface TPackagingDispatchInsufficientInventoryError {
  message: string;
  data: IPackagingInsufficientInventoryItem[] | null;
}

export const createPackagingDispatch = createAsyncThunk(
  'packagingDispatch/create',
  async (
    params: { dispatchTransaction: TCreatePackagingDispatch },
    { rejectWithValue }
  ) => {
    const { dispatchTransaction } = params;
    try {
      const response = await axios.post('packaging/dispatch', dispatchTransaction);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue({ message: err.message, data: err.data ?? null });
    }
  }
);

export const updatePackagingDispatch = createAsyncThunk(
  'packagingDispatch/update',
  async (
    params: { dispatchId: string; dispatchTransaction: TUpdatePackagingDispatch },
    { rejectWithValue }
  ) => {
    const { dispatchId, dispatchTransaction } = params;
    try {
      const response = await axios.put(`packaging/dispatch/${dispatchId}`, dispatchTransaction);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue({ message: err.message, data: err.data ?? null });
    }
  }
);
